import {
    getPartnerReferrers,
    getRolesAndPermissions,
    logout,
} from '~/api/auth';
import { useAuthStore } from '~/store/auth';
import type {
    LogoutResponse,
    PartnerReferrersResponse,
    RolesAndPermissionsResponse,
} from '~/types/api';
import { defaultFilters, filtersKey } from '~/utils/constants';
import { resultOf } from '~/utils/fp-results';
import notify from '~/utils/useNotification';

export const loadUser = async () => {
    const { setAuthData } = useAuthStore.getState();

    const [ok, userData]: [boolean, RolesAndPermissionsResponse] =
        await resultOf(getRolesAndPermissions());

    if (ok && userData.success) {
        setAuthData({
            isAuthenticated: true,
            user: userData?.user,
            email: userData?.user?.email,
            roles: Array.isArray(userData?.user?.roles)
                ? userData?.user?.roles
                : [],
            permissions: userData?.user?.permissions,
        });
        return true;
    }

    notify({
        type: 'error',
        title: 'Error loading user',
        description:
            userData.message || 'Failed to load user. Please try again.',
    });
    return false;
};

export const getPartnersAndReferrers = async () => {
    const { setPartners } = useAuthStore.getState();
    const [ok, partnersData]: [boolean, PartnerReferrersResponse] =
        await resultOf(getPartnerReferrers());

    /* succes is not send in the Api response so, using data here for now, but will be fixed in the future */
    if (!ok || !partnersData?.data) {
        notify({
            type: 'error',
            title: 'Error loading partners',
            description:
                partnersData?.message ||
                'An error occurred while loading partners. Please try again.',
        });
        return [];
    }

    // If there's data, treat it as a success and update state
    setPartners(partnersData.data || []);
    return partnersData.data; // Return data on success
};

export const logoutUser = async () => {
    const { resetAuthData } = useAuthStore.getState();

    const [ok, res]: [boolean, LogoutResponse] = await resultOf(logout());

    // A 401 means the session had already expired, so the user is effectively logged out.
    const sessionExpired = !ok && res.status === 401;

    if ((ok && res.success) || sessionExpired) {
        notify({
            type: 'warning',
            title: 'Logged out successfully',
            description:
                (ok && res.message) || 'You have been logged out.',
        });
        sessionStorage.setItem(filtersKey, JSON.stringify(defaultFilters));
        resetAuthData();
    } else {
        notify({
            type: 'error',
            title: 'Error logging out',
            description:
                res.message ||
                'An error occurred while logging out. Please try again.',
        });
    }
};
